<template>
  <NModal class="max-w-[800px] rounded-2" :on-esc="onClose" :on-update:show="onClose" show>
    <NCard class="rounded-2">
      <template #header>
        <div class="text-lg font-bold">
          {{ t('canvasUI.browserApp.fileSmartFolders.configureTitle') }}
        </div>
      </template>
      <div class="flex flex-col h-[60vh]">
        <NSpin v-if="store.state.isLoading" class="flex-grow flex items-center justify-center" />
        <div v-else class="flex flex-col h-full">
          <div class="flex-grow overflow-auto">
            <div class="font-500 tracking-[1.25px] my-4">
              {{ t('canvasUI.browserApp.fileSmartFolders.configureDescription') }}
            </div>
            <VueDraggable v-model="enabledLevels" handle=".drag-handle">
              <div
                v-for="level in enabledLevels"
                :key="level.accessor"
                class="flex flex-col justify-center min-h-[40px] color-text2 mb-2"
              >
                <div class="flex items-center">
                  <CIcon class="drag-handle mr-2 cursor-move" icon="grip-dots-vertical" size="20" />
                  <div class="flex-grow border border-solid border-primary4 border-rounded-md pa-2 bg-base">
                    <div class="flex items-center justify-between">
                      <div class="font-500 color-primary">
                        {{ level.display_name }}
                      </div>
                      <CButton circle :disabled="store.state.isSavingConfig" icon @click="disableLevel(level)">
                        <CIcon icon="minus" size="16px" />
                      </CButton>
                    </div>
                  </div>
                </div>
              </div>
            </VueDraggable>
            <div v-if="!enabledLevels.length" class="text-center color-text3">
              {{ t('canvasUI.browserApp.fileSmartFolders.noLevelsSelected') }}
            </div>

            <div class="font-500 tracking-[1.25px] my-4">
              {{ t('canvasUI.browserApp.fileSmartFolders.availableLevels') }}
            </div>
            <div class="flex flex-col gap-2">
              <div
                v-for="level in disabledLevels"
                :key="level.accessor"
                class="flex flex-col justify-center min-h-[40px] color-text2"
              >
                <div
                  class="flex items-center justify-between border border-solid border-primary4 border-rounded-md pa-2"
                >
                  <div class="font-500">{{ level.display_name }}</div>
                  <CButton
                    circle
                    :disabled="store.state.isSavingConfig"
                    icon
                    type="primary"
                    @click="enableLevel(level)"
                  >
                    <CIcon icon="plus" size="16px" />
                  </CButton>
                </div>
              </div>
            </div>
            <div v-if="!disabledLevels.length" class="text-center py-4 color-text3">
              {{ t('canvasUI.browserApp.fileSmartFolders.noLevelsToAdd') }}
            </div>
          </div>
        </div>
      </div>
      <template #footer>
        <div class="flex justify-between gap-4">
          <CButton :disabled="store.state.isSavingConfig" @click="onClose">
            {{ t('canvasUI.common.cancel') }}
          </CButton>
          <CButton :loading="store.state.isSavingConfig" type="primary" @click="onSave">
            {{ t('canvasUI.common.save') }}
          </CButton>
        </div>
      </template>
    </NCard>
  </NModal>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { NModal, NCard, NSpin } from 'naive-ui'
import { VueDraggable } from 'vue-draggable-plus'
import { useI18n } from 'vue-i18n'

import useFileSmartFolderStore, { type SmartFolderProperty } from '@browser/stores/fileSmartFolderStore'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CButton from '@lib/components/CButton/CButton.vue'

const emit = defineEmits<{
  close: []
}>()

const { t } = useI18n()
const store = useFileSmartFolderStore()

const enabledLevels = ref<SmartFolderProperty[]>([...(store.state.hierarchyLevels || [])])

const disabledLevels = computed(() =>
  store.allSmartFolderProperties.value.filter(
    (level) => !enabledLevels.value.some((enabled) => enabled.accessor === level.accessor),
  ),
)

function disableLevel(level: SmartFolderProperty) {
  enabledLevels.value = enabledLevels.value.filter((l) => l.accessor !== level.accessor)
}

function enableLevel(level: SmartFolderProperty) {
  enabledLevels.value.push(level)
}

async function onSave() {
  await store.actions.saveConfiguration({
    enabledProperties: enabledLevels.value,
  })
  emit('close')
}

function onClose() {
  emit('close')
}
</script>
